"use client"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import { Alert, AlertDescription } from "../../components/ui/alert"
import { Badge } from "../../components/ui/badge"
import { Info, ArrowRight } from "lucide-react"
import { roundPercentage, normalizePercentages, distributePercentageEqually } from "../../lib/percentage-utils"

interface SmartDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  beneficiaryName: string
  beneficiaryPercentage: number
  remainingBeneficiaries: Array<{ id: number; name: string; percentage: number }>
  totalAfterDeletion: number
}

export function SmartDeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  beneficiaryName,
  beneficiaryPercentage,
  remainingBeneficiaries,
  totalAfterDeletion,
}: SmartDeleteDialogProps) {
  const handleConfirm = () => {
    onConfirm()
    onOpenChange(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const currentTotal = remainingBeneficiaries.reduce((sum, b) => sum + b.percentage, 0)
  const totalAfterRedistribution = currentTotal + beneficiaryPercentage
  const willExceed100 = totalAfterRedistribution > 100

  const existingPercentages = remainingBeneficiaries.map((b) => b.percentage)
  const newPercentages = willExceed100
    ? normalizePercentages(existingPercentages)
    : distributePercentageEqually(beneficiaryPercentage, remainingBeneficiaries.length, existingPercentages)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-[var(--color-z-blue)]" />
            Distribución Inteligente de Porcentajes
          </DialogTitle>
          <DialogDescription>
            Al eliminar a <strong>{beneficiaryName}</strong> con {beneficiaryPercentage}% de cobertura, el porcentaje se
            redistribuirá automáticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert className="border-[var(--color-z-blue)]/30 bg-[var(--color-z-blue)]/5">
            <Info className="h-4 w-4 text-[var(--color-z-blue)]" />
            <AlertDescription className="text-sm">
              {willExceed100 ? (
                <>
                  El total actual supera el 100%. Los porcentajes se normalizarán para que sumen exactamente{" "}
                  <strong>100%</strong>, distribuyendo proporcionalmente entre los {remainingBeneficiaries.length}{" "}
                  beneficiario(s) restante(s).
                </>
              ) : (
                <>
                  El {beneficiaryPercentage}% se distribuirá equitativamente entre los {remainingBeneficiaries.length}{" "}
                  beneficiario(s) restante(s), agregando{" "}
                  <strong>+{roundPercentage(beneficiaryPercentage / remainingBeneficiaries.length)}%</strong> a cada
                  uno.
                </>
              )}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <p className="text-sm font-medium text-[var(--color-z-blue)]">Distribución resultante:</p>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {remainingBeneficiaries.map((b, index) => (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                  <span className="text-sm font-medium truncate flex-1 mr-2">{b.name}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="text-xs">
                      {b.percentage}%
                    </Badge>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <Badge variant="default" className="text-xs bg-green-600">
                      {newPercentages[index]}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Alert className="border-green-200 bg-green-50">
            <AlertDescription className="text-sm text-green-800">
              <strong>Total después de la redistribución: 100%</strong>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} className="bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]">
            Entendido, Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
